import { useState, useEffect, useCallback } from "react";
import { useDropzone } from "react-dropzone";
import API_ENDPOINTS from "../utilities/APIRoutes";

const useDataManagement = (
  AuthToken,
  UserToken,
  showSnackbar,
  updateTotalTokensUsed
) => {
  const [loading, setLoading] = useState(false);
  const [selectionType, setSelectionType] = useState("collections");
  const [collections, setCollections] = useState([]);
  const [databases, setDatabases] = useState([]);
  const [selectedCollections, setSelectedCollections] = useState([]);
  const [pendingDatabaseId, setPendingDatabaseId] = useState(null);
  const [selectedDatabase, setSelectedDatabase] = useState("");
  const [availableCollections, setAvailableCollections] = useState([]);

  const [files, setFiles] = useState([]);
  const [collectionFilesMap, setCollectionFilesMap] = useState({});
  const [selectedFile, setSelectedFile] = useState([]);
  const [selectedURL, setSelectedURL] = useState("");

  // Delete modal state
  const [open, setOpen] = useState(false);
  const [fileToDelete, setFileToDelete] = useState(null);

  // Per-row download in-flight tracker (FileIDs currently being downloaded).
  // Uses a Set so multiple concurrent downloads each get their own spinner.
  const [downloadingFileIds, setDownloadingFileIds] = useState(new Set());

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const filesPerPage = 5;

  // Add missing state for error handling
  const [error, setError] = useState(false);
  const [helperText, setHelperText] = useState("");

  const fetchCollections = useCallback(async () => {
    if (!AuthToken || !UserToken) return;
    try {
      const response = await fetch(API_ENDPOINTS.ListCollections, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          "User-Token": UserToken,
          Authorization: AuthToken,
        },
      });

      if (!response.ok) throw new Error("Failed to fetch collections");

      const data = await response.json();
      const publicCollections = (data.message.publicResults || []).map(c => ({ ...c, isPublic: true }));
      const privateCollections = (data.message.privateResults || []).map(c => ({ ...c, isPublic: false }));
      const allCollections = [...publicCollections, ...privateCollections];

      const databaseTypes = [
        "nosql_chat",
        "dynamodb",
        "sql_chat",
        "csv_chat",
      ];
      const dbs = allCollections.filter((col) =>
        databaseTypes.includes(col.collectionType)
      );
      const regularCollections = allCollections.filter(
        (col) => !databaseTypes.includes(col.collectionType)
      );

      setDatabases(dbs);
      setCollections(regularCollections);
    } catch (error) {
      showSnackbar("Error fetching collections", "error");
      console.error("Error fetching collections:", error);
    }
  }, [AuthToken, UserToken, showSnackbar]);

  useEffect(() => {
    fetchCollections();
  }, [fetchCollections]);

  const fetchFiles = useCallback(
    async (collectionIds) => {
      try {
        const responses = await Promise.all(
          collectionIds.map((collectionId) =>
            fetch(API_ENDPOINTS.RetrieveCollection, {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                "User-Token": UserToken,
                Authorization: AuthToken,
              },
              body: JSON.stringify({ id: collectionId }),
            })
          )
        );

        const allFilesData = await Promise.all(
          responses.map((response) => {
            if (!response.ok) throw new Error("Failed to fetch files");
            return response.json();
          })
        );

        const map = {};
        collectionIds.forEach((id, i) => {
          map[id] = allFilesData[i].message?.Files || [];
        });
        setCollectionFilesMap(map);

        const fetchedFiles = allFilesData.flatMap(
          (data) => data.message?.Files || []
        );
        setFiles(fetchedFiles);
      } catch (error) {
        showSnackbar("Error fetching files", "error");
        console.error("Error fetching files:", error);
        setFiles([]);
      }
    },
    [AuthToken, UserToken, showSnackbar]
  );

  const handleSelectionTypeChange = (event, setChatHistory) => {
    const newType = event.target.value;
    setSelectionType(newType);
    setSelectedCollections([]);
    setSelectedDatabase("");
    setFiles([]);
    setCollectionFilesMap({});
    if (setChatHistory) setChatHistory([]);
  };

  const handleSelectionTypeChangeWithoutReset = (event) => {
    const newType = event.target.value;
    setSelectionType(newType);
  };

  const handleCollectionChange = async (event, setChatHistory) => {
    const newSelectedCollections = event.target.value;
    setSelectedCollections(newSelectedCollections);
    if (setChatHistory) setChatHistory([]);

    if (newSelectedCollections.length > 0) {
      const collectionIds = newSelectedCollections.map((c) => c.id);
      await fetchFiles(collectionIds);
    } else {
      setFiles([]);
      setCollectionFilesMap({});
    }
  };

  const handleDatabaseChange = async (event) => {
    const db = event.target.value;
    setSelectedDatabase(db);
    setLoading(true);

    const endpoints = {
      nosql_chat: API_ENDPOINTS.GetNoSQLCollections,
      sql_chat: API_ENDPOINTS.GetSQLTables,
      dynamodb: API_ENDPOINTS.GetDynamoFields,
    };
    const endpointTemplate = endpoints[db.collectionType];
    if (!endpointTemplate) {
      showSnackbar("Unsupported database type.", "error");
      setLoading(false);
      return;
    }
    const endpoint = endpointTemplate.replace("{collection_id}", db.id);

    try {
      const response = await fetch(endpoint, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          "authorization-token": AuthToken,
          "user-token": UserToken,
        },
      });

      if (!response.ok)
        throw new Error("Failed to fetch details for this database.");

      const data = await response.json();
      const collectionsOrTables =
        data.available_collections || data.available_tables || data || [];
      setAvailableCollections(collectionsOrTables);
    } catch (error) {
      showSnackbar("Error fetching database details.", "error");
      console.error("Error:", error);
      setAvailableCollections([]);
    } finally {
      setLoading(false);
    }
  };

  const onDrop = useCallback((acceptedFiles) => {
    setSelectedFile((prevFiles) => [...prevFiles, ...acceptedFiles]);
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: "application/pdf,.docx,.txt",
    onDrop,
  });

  const removeFile = (fileIndex) => {
    setSelectedFile((prevFiles) =>
      prevFiles.filter((_, index) => index !== fileIndex)
    );
  };

  // Add the missing handleUrlChange function
  const handleUrlChange = (e) => {
    const value = e.target.value;
    setSelectedURL(value);

    if (value && !value.startsWith("https://")) {
      setError(true);
      setHelperText('URL must start with "https://"');
    } else {
      setError(false);
      setHelperText("");
    }
  };

  const handleUploadClick = async () => {
    const maxFileSize = 50 * 1024 * 1024; // 50 MB
    const oversizedFile = selectedFile.find((file) => file.size > maxFileSize);
    if (oversizedFile) {
      showSnackbar(
        `File "${oversizedFile.name}" exceeds the 50 MB size limit.`,
        "error"
      );
      return;
    }

    // Fix: Check for empty string instead of array length
    if (
      (selectedFile.length === 0 && selectedURL.trim() === "") ||
      selectedCollections.length !== 1
    ) {
      showSnackbar(
        "Please select one collection and at least one file or URL to upload.",
        "error"
      );
      return;
    }

    setLoading(true);
    const formData = new FormData();
    selectedFile.forEach((file) => formData.append("files", file));

    // Fix: Check for non-empty string before splitting
    if (selectedURL && selectedURL.trim()) {
      const url_list = selectedURL
        .split(",")
        .map((u) => u.trim())
        .filter((u) => u);
      if (url_list.length > 0) formData.append("urls", url_list);
    }

    formData.append("collection_id", selectedCollections[0].id);
    formData.append("collection_name", selectedCollections[0].name);

    try {
      const response = await fetch(API_ENDPOINTS.UploadDocAI, {
        method: "POST",
        headers: { "user-token": UserToken, "authorization-token": AuthToken },
        body: formData,
      });

      const data = await response.json();

      if (data.success === true) {
        if (updateTotalTokensUsed && data.token_usage) {
          updateTotalTokensUsed(data.token_usage);
        }
        setSelectedFile([]);
        setSelectedURL("");
        fetchFiles(selectedCollections.map((c) => c.id));

        // Backend now returns failed_urls: [{ url, reason }, ...] when partial
        // URL failures occur. Surface them so the user knows why an entry
        // they thought they uploaded isn't in their list.
        const failedUrls = Array.isArray(data.failed_urls) ? data.failed_urls : [];
        if (failedUrls.length > 0) {
          const failuresText = failedUrls
            .map((f) => `${f.url} (${f.reason})`)
            .join("; ");
          showSnackbar(
            `Uploaded with ${failedUrls.length} URL failure(s): ${failuresText}`,
            "warning"
          );
        } else {
          showSnackbar("Uploaded successfully!", "success");
        }
      } else {
        throw new Error(data.message || "Upload failed due to a backend error.");
      }
    } catch (error) {
      showSnackbar(error.message || "Error uploading document", "error");
      console.error("Error uploading document:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleClickOpen = (file) => {
    setFileToDelete(file);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setFileToDelete(null);
  };

  const handleConfirmDelete = async () => {
    if (!fileToDelete || selectedCollections.length !== 1) {
      showSnackbar(
        "Please select exactly one collection to delete from.",
        "error"
      );
      return;
    }
    setLoading(true);
    try {
      const response = await fetch(API_ENDPOINTS.DeleteDocAI, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          "user-token": UserToken,
          "authorization-token": AuthToken,
        },
        body: JSON.stringify({
          collection_name: selectedCollections[0].name,
          document_name: fileToDelete.FileName,
          document_id: fileToDelete.FileID,
        }),
      });

      if (!response.ok) throw new Error("Failed to delete file");

      setFiles(files.filter((f) => f.FileID !== fileToDelete.FileID));
      showSnackbar("File deleted successfully!", "success");
    } catch (error) {
      showSnackbar("Error deleting file", "error");
      console.error("Error deleting file:", error);
    } finally {
      setLoading(false);
      handleClose();
    }
  };

  const handleDownloadFile = async (file, collectionId) => {
    const targetCollectionId =
      collectionId ||
      (selectedCollections.length === 1 ? selectedCollections[0].id : null);
    if (!targetCollectionId) {
      showSnackbar(
        "Please select exactly one collection to download from.",
        "error"
      );
      return;
    }
    if (!file?.FileName || !file?.FileID) return;

    setDownloadingFileIds((prev) => {
      const next = new Set(prev);
      next.add(file.FileID);
      return next;
    });

    try {
      const url =
        `${API_ENDPOINTS.DownloadFileAI}` +
        `?collection_id=${encodeURIComponent(targetCollectionId)}` +
        `&filename=${encodeURIComponent(file.FileName)}`;

      const response = await fetch(url, {
        method: "GET",
        headers: {
          "user-token": UserToken,
          "authorization-token": AuthToken,
        },
      });

      if (!response.ok) {
        if (response.status === 401) {
          // No global re-auth interceptor in this app — surface the same
          // session-expired toast every other endpoint relies on.
          showSnackbar("Session expired. Please sign in again.", "error");
        } else if (response.status === 403) {
          showSnackbar(
            "You don't have permission to access this file",
            "error"
          );
        } else if (response.status === 404) {
          showSnackbar("File not found", "error");
        } else {
          showSnackbar("Download is currently unavailable", "error");
        }
        return;
      }

      // Prefer Content-Disposition's filename so the saved name matches what
      // the backend marked as canonical; fall back to the row's FileName.
      let downloadName = file.FileName;
      const disposition = response.headers.get("Content-Disposition");
      if (disposition) {
        const match =
          /filename\*=(?:UTF-8'')?["']?([^;"'\r\n]+)["']?/i.exec(disposition) ||
          /filename=["']?([^;"'\r\n]+)["']?/i.exec(disposition);
        if (match && match[1]) {
          try {
            downloadName = decodeURIComponent(match[1]);
          } catch {
            downloadName = match[1];
          }
        }
      }

      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = blobUrl;
      anchor.download = downloadName;
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
      setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
    } catch (error) {
      showSnackbar("Download is currently unavailable", "error");
      console.error("Error downloading file:", error);
    } finally {
      setDownloadingFileIds((prev) => {
        const next = new Set(prev);
        next.delete(file.FileID);
        return next;
      });
    }
  };

  const paginatedFiles = files.slice(
    (currentPage - 1) * filesPerPage,
    currentPage * filesPerPage
  );
  const handlePageChange = (event, value) => setCurrentPage(value);

  return {
    loading,
    setLoading,
    selectionType,
    setSelectionType,
    collections,
    databases,
    selectedCollections,
    setSelectedCollections,
    pendingDatabaseId,
    setPendingDatabaseId,
    selectedDatabase,
    setSelectedDatabase,
    availableCollections,
    setAvailableCollections,
    files,
    setFiles,
    collectionFilesMap,
    selectedFile,
    setSelectedFile,
    selectedURL,
    handleUrlChange,

    handleSelectionTypeChange,
    handleSelectionTypeChangeWithoutReset,
    handleCollectionChange,
    handleDatabaseChange,

    // File upload
    getRootProps,
    getInputProps,
    isDragActive,
    removeFile,
    handleUploadClick,

    // File list and deletion
    paginatedFiles,
    filesPerPage,
    currentPage,
    handlePageChange,
    open,
    fileToDelete,
    handleClickOpen,
    handleClose,
    handleConfirmDelete,
    error,
    helperText,
    fetchCollections,
    fetchFiles,

    // File download
    handleDownloadFile,
    downloadingFileIds,
  };
};

export default useDataManagement;
